import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { usuariosService } from '../services/api';
import { 
  FiPlus, 
  FiSearch, 
  FiUser, 
  FiShield, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiEyeOff,
  FiLock,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Usuarios = () => {
  const [filtros, setFiltros] = useState({
    rol: '',
    activo: '',
    buscar: ''
  });
  
  const [paginacion, setPaginacion] = useState({
    pagina: 1,
    limite: 25 // Aumentado de 10 a 25 para reducir número de peticiones
  });

  const [modalCrear, setModalCrear] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [modalCambiarPassword, setModalCambiarPassword] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [mostrarPasswordConfirm, setMostrarPasswordConfirm] = useState(false);

  const [formData, setFormData] = useState({
    nombre_usuario: '',
    nombre_completo: '',
    email: '',
    rol: 'operador',
    activo: true,
    password: '',
    password_confirm: ''
  });

  const [passwordData, setPasswordData] = useState({
    password_nuevo: '',
    password_confirm: ''
  });

  const queryClient = useQueryClient();

  // Obtener usuarios
  const { data, isLoading, error } = useQuery(
    ['usuarios', filtros, paginacion],
    async () => {

      const params = {
        ...paginacion,
        ...filtros
      };
      
      // Solo incluir filtros que tengan valor
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      try {
        const response = await usuariosService.obtenerTodos(params);
        return response.data;
      } catch (error) {
        console.error('❌ Error al obtener usuarios:', error);
        throw error;
      }
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('❌ Error en consulta de usuarios:', error);
      }
    }
  );

  // Obtener estadísticas
  const { data: estadisticas, isLoading: cargandoEstadisticas, error: errorEstadisticas } = useQuery(
    'estadisticas-usuarios',
    async () => {
      try {
        const response = await usuariosService.estadisticas();
        return response.data.estadisticas;
      } catch (error) {
        console.error('❌ Error al obtener estadísticas:', error);
        throw error;
      }
    },
    {
      onError: (error) => {
        console.error('❌ Error en consulta de estadísticas:', error);
      }
    }
  );

  // Mutaciones
  const crearUsuario = useMutation(
    async (datos) => {
      const response = await usuariosService.crear(datos);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        setModalCrear(false);
        resetFormData();
        queryClient.invalidateQueries(['usuarios']);
        queryClient.invalidateQueries('estadisticas-usuarios');
      },
      onError: (error) => {
        const msg = error.response?.data?.mensaje || error.response?.data?.error || 'Error al crear usuario';
        toast.error(msg);
      }
    }
  );

  const actualizarUsuario = useMutation(
    async ({ id, datos }) => {
      const response = await usuariosService.actualizar(id, datos);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        setModalEditar(false);
        setUsuarioSeleccionado(null);
        queryClient.invalidateQueries(['usuarios']);
        queryClient.invalidateQueries('estadisticas-usuarios');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar usuario');
      }
    }
  );

  const eliminarUsuario = useMutation(
    async (id) => {
      const response = await usuariosService.eliminar(id);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        setModalEliminar(false);
        setUsuarioSeleccionado(null);
        queryClient.invalidateQueries(['usuarios']);
        queryClient.invalidateQueries('estadisticas-usuarios');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al eliminar usuario');
      }
    }
  );

  const cambiarPassword = useMutation(
    async ({ id, datos }) => {
      const response = await usuariosService.cambiarContrasena(id, datos);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        setModalCambiarPassword(false);
        setUsuarioSeleccionado(null);
        resetPasswordData();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al cambiar contraseña');
      }
    }
  );

  const usuarios = data?.usuarios || [];
  const paginacionData = data?.paginacion || {};

  // Funciones de manejo
  const manejarCambioFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setPaginacion(prev => ({ ...prev, pagina: 1 }));
  };

  const handleCambiarPagina = (nuevaPagina) => {
    setPaginacion(prev => ({ ...prev, pagina: nuevaPagina }));
  };

  const handleCambiarLimite = (nuevoLimite) => {
    setPaginacion({ pagina: 1, limite: nuevoLimite });
  };

  const resetFormData = () => {
    setFormData({
      nombre_usuario: '',
      nombre_completo: '',
      email: '',
      rol: 'operador',
      activo: true,
      password: '',
      password_confirm: ''
    });
    setMostrarPassword(false);
    setMostrarPasswordConfirm(false);
  };

  const resetPasswordData = () => {
    setPasswordData({
      password_nuevo: '',
      password_confirm: ''
    });
    setMostrarPassword(false);
    setMostrarPasswordConfirm(false);
  };

  const abrirModalEditar = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setFormData({
      nombre_usuario: usuario.nombre_usuario,
      nombre_completo: usuario.nombre_completo || '',
      email: usuario.email,
      rol: usuario.rol,
      activo: usuario.activo,
      password: '',
      password_confirm: ''
    });
    setModalEditar(true);
  };

  const abrirModalEliminar = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setModalEliminar(true);
  };

  const abrirModalCambiarPassword = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setModalCambiarPassword(true);
  };

  const handleCrearUsuario = (e) => {
    e.preventDefault();
    if (formData.password !== formData.password_confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    const datosCrear = {
      nombre_usuario: formData.nombre_usuario,
      nombre_completo: formData.nombre_completo || formData.nombre_usuario,
      email: formData.email,
      rol: formData.rol,
      contrasena: formData.password
    };
    crearUsuario.mutate(datosCrear);
  };

  const handleActualizarUsuario = (e) => {
    e.preventDefault();
    const datosActualizar = { ...formData };
    delete datosActualizar.password;
    delete datosActualizar.password_confirm;
    actualizarUsuario.mutate({ id: usuarioSeleccionado.id, datos: datosActualizar });
  };

  const handleCambiarPassword = (e) => {
    e.preventDefault();
    if (passwordData.password_nuevo !== passwordData.password_confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    cambiarPassword.mutate({ 
      id: usuarioSeleccionado.id, 
      datos: {
        contrasena_nueva: passwordData.password_nuevo
      }
    });
  };

  const obtenerRolClases = (rol) => {
    switch (rol) {
      case 'admin':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      case 'operador':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
      case 'supervisor':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  };

  const obtenerEstadoClases = (activo) => {
    return activo 
      ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
      : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error al cargar los usuarios</p>
        <p className="text-sm text-gray-500 mt-2">
          {error.response?.data?.error || error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administra usuarios del sistema MDM</p>
        </div>
        <button
          onClick={() => setModalCrear(true)}
          className="btn-primary flex items-center"
        >
          <FiPlus className="mr-2" />
          Nuevo Usuario
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cargandoEstadisticas ? (
          // Mostrar skeleton loading para las 4 tarjetas
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
                <div className="p-3 rounded-lg bg-gray-200 animate-pulse">
                  <div className="w-5 h-5"></div>
                </div>
              </div>
            </div>
          ))
        ) : errorEstadisticas ? (
          // Mostrar error
          <div className="col-span-full bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FiShield className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error al cargar estadísticas
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {errorEstadisticas.message || 'No se pudieron cargar las estadísticas'}
                </p>
              </div>
            </div>
          </div>
        ) : estadisticas ? (
          // Mostrar estadísticas
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
                  <p className="text-2xl font-bold text-gray-900">{estadisticas.total_usuarios || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-primary-500 text-white">
                  <FiUser size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Activos</p>
                  <p className="text-2xl font-bold text-green-600">{estadisticas.usuarios_activos || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500 text-white">
                  <FiUser size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Administradores</p>
                  <p className="text-2xl font-bold text-red-600">{estadisticas.administradores || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-500 text-white">
                  <FiShield size={20} />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Operadores</p>
                  <p className="text-2xl font-bold text-blue-600">{estadisticas.operadores || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-500 text-white">
                  <FiUser size={20} />
                </div>
              </div>
            </div>
          </>
        ) : (
          // Estado vacío
          <div className="col-span-full bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">No hay estadísticas disponibles</p>
            </div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                className="input-field pl-10"
                value={filtros.buscar}
                onChange={(e) => manejarCambioFiltro('buscar', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol
            </label>
            <select
              className="input-field"
              value={filtros.rol}
              onChange={(e) => manejarCambioFiltro('rol', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="admin">Administrador</option>
              <option value="operador">Operador</option>
              <option value="supervisor">Supervisor</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              className="input-field"
              value={filtros.activo}
              onChange={(e) => manejarCambioFiltro('activo', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mostrar
            </label>
            <select
              className="input-field"
              value={paginacion.limite}
              onChange={(e) => handleCambiarLimite(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de usuarios */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Creación
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {usuario.nombre_usuario}
                      </div>
                      <div className="text-sm text-gray-500">
                        {usuario.email}
                      </div>
                      <div className="text-sm text-gray-400">
                        ID: {usuario.id}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={obtenerRolClases(usuario.rol)}>
                      {usuario.rol === 'admin' && <FiShield size={12} className="mr-1" />}
                      {usuario.rol === 'operador' && <FiUser size={12} className="mr-1" />}
                      {usuario.rol === 'supervisor' && <FiShield size={12} className="mr-1" />}
                      {usuario.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={obtenerEstadoClases(usuario.activo)}>
                      {usuario.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {usuario.fecha_creacion 
                      ? new Date(usuario.fecha_creacion).toLocaleDateString('es-ES')
                      : 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => abrirModalCambiarPassword(usuario)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Cambiar contraseña"
                      >
                        <FiLock size={16} />
                      </button>
                      <button
                        onClick={() => abrirModalEditar(usuario)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar usuario"
                      >
                        <FiEdit size={16} />
                      </button>
                      <button
                        onClick={() => abrirModalEliminar(usuario)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar usuario"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {paginacionData && paginacionData.paginas > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleCambiarPagina(paginacion.pagina - 1)}
                disabled={paginacion.pagina === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handleCambiarPagina(paginacion.pagina + 1)}
                disabled={paginacion.pagina === paginacionData.paginas}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">{((paginacion.pagina - 1) * paginacion.limite) + 1}</span>
                  {' '}a{' '}
                  <span className="font-medium">
                    {Math.min(paginacion.pagina * paginacion.limite, paginacionData.total)}
                  </span>
                  {' '}de{' '}
                  <span className="font-medium">{paginacionData.total}</span>
                  {' '}resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handleCambiarPagina(paginacion.pagina - 1)}
                    disabled={paginacion.pagina === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Anterior</span>
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Números de página */}
                  {Array.from({ length: Math.min(5, paginacionData.paginas) }, (_, i) => {
                    let pagina;
                    if (paginacionData.paginas <= 5) {
                      pagina = i + 1;
                    } else if (paginacion.pagina <= 3) {
                      pagina = i + 1;
                    } else if (paginacion.pagina >= paginacionData.paginas - 2) {
                      pagina = paginacionData.paginas - 4 + i;
                    } else {
                      pagina = paginacion.pagina - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pagina}
                        onClick={() => handleCambiarPagina(pagina)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagina === paginacion.pagina
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pagina}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handleCambiarPagina(paginacion.pagina + 1)}
                    disabled={paginacion.pagina === paginacionData.paginas}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Siguiente</span>
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Crear Usuario */}
      {modalCrear && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Crear Nuevo Usuario</h3>
              <form onSubmit={handleCrearUsuario}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de Usuario</label>
                  <input
                    type="text"
                    value={formData.nombre_usuario}
                    onChange={(e) => setFormData({ ...formData, nombre_usuario: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre completo</label>
                  <input
                    type="text"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                    className="input-field"
                    placeholder="Ej. Ariana Janela"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                  <select
                    value={formData.rol}
                    onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="operador">Operador</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                  <div className="relative">
                    <input
                      type={mostrarPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarPassword(!mostrarPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Contraseña</label>
                  <div className="relative">
                    <input
                      type={mostrarPasswordConfirm ? "text" : "password"}
                      value={formData.password_confirm}
                      onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarPasswordConfirm(!mostrarPasswordConfirm)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarPasswordConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Usuario activo</span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setModalCrear(false);
                      resetFormData();
                    }}
                    className="btn-outline"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={crearUsuario.isLoading}
                    className="btn-primary"
                  >
                    {crearUsuario.isLoading ? 'Creando...' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Usuario */}
      {modalEditar && usuarioSeleccionado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Usuario</h3>
              <form onSubmit={handleActualizarUsuario}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de Usuario</label>
                  <input
                    type="text"
                    value={formData.nombre_usuario}
                    onChange={(e) => setFormData({ ...formData, nombre_usuario: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre completo</label>
                  <input
                    type="text"
                    value={formData.nombre_completo}
                    onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                    className="input-field"
                    placeholder="Ej. Ariana Janela"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                  <select
                    value={formData.rol}
                    onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="operador">Operador</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Usuario activo</span>
                  </label>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setModalEditar(false);
                      setUsuarioSeleccionado(null);
                    }}
                    className="btn-outline"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={actualizarUsuario.isLoading}
                    className="btn-primary"
                  >
                    {actualizarUsuario.isLoading ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar Usuario */}
      {modalEliminar && usuarioSeleccionado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Eliminar Usuario</h3>
              <p className="text-sm text-gray-600 mb-4">
                ¿Estás seguro de que quieres eliminar al usuario <strong>{usuarioSeleccionado.nombre_usuario}</strong>?
                Esta acción no se puede deshacer.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setModalEliminar(false);
                    setUsuarioSeleccionado(null);
                  }}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => eliminarUsuario.mutate(usuarioSeleccionado.id)}
                  disabled={eliminarUsuario.isLoading}
                  className="btn-danger"
                >
                  {eliminarUsuario.isLoading ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cambiar Contraseña */}
      {modalCambiarPassword && usuarioSeleccionado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cambiar Contraseña</h3>
              <p className="text-sm text-gray-600 mb-4">
                Cambiar contraseña para <strong>{usuarioSeleccionado.nombre_usuario}</strong>
              </p>
                             <form onSubmit={handleCambiarPassword}>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
                  <div className="relative">
                    <input
                      type={mostrarPasswordConfirm ? "text" : "password"}
                      value={passwordData.password_nuevo}
                      onChange={(e) => setPasswordData({ ...passwordData, password_nuevo: e.target.value })}
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarPasswordConfirm(!mostrarPasswordConfirm)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarPasswordConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Nueva Contraseña</label>
                  <div className="relative">
                    <input
                      type={mostrarPasswordConfirm ? "text" : "password"}
                      value={passwordData.password_confirm}
                      onChange={(e) => setPasswordData({ ...passwordData, password_confirm: e.target.value })}
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarPasswordConfirm(!mostrarPasswordConfirm)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {mostrarPasswordConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setModalCambiarPassword(false);
                      setUsuarioSeleccionado(null);
                      resetPasswordData();
                    }}
                    className="btn-outline"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={cambiarPassword.isLoading}
                    className="btn-primary"
                  >
                    {cambiarPassword.isLoading ? 'Cambiando...' : 'Cambiar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios; 